import React, { useEffect, useRef, useState } from 'react';
import { FinancialMetricsTable } from './FinancialMetricsTable';
import { StandardFinancialTable } from './StandardFinancialTable';
// import { financialData as fd } from './financialData';
// import { BarChart2, Eye, EyeOff, ChevronUp, ChevronDown, Image } from 'lucide-react';
import FinancialMetricChart from '../charts/FinancialMetricChart';
import LoaderHelper from '../../utility/Loading/LoaderHelper';
import StockService from '../../Api_Module/Api_Services/StockService';
import { FinancialData } from './types';
import useViewportHeight from '../../helper/useViewportHeight';

interface CashFlowHistoryProps {
    // isChart: boolean;
    symbol?: string;
}

const CashFlowHistory: React.FC<CashFlowHistoryProps> = ({ symbol }) => {
    const [selectedMetrics, setSelectedMetrics] = useState<string[]>(["netIncome"]);
    const [type, setType] = useState<string>('annual');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [viewMode, setViewMode] = useState<'standard' | 'detailed'>('standard');
    const [financialData, setFinancialData] = useState<FinancialData>({
        periods: [],
        metrics: []
    });
    const [tableFinancialData, setTableFinancialData] = useState<FinancialData>({
        periods: [],
        metrics: []
    });
    // const [availableMetrics, setAvailableMetrics] = useState<any[]>([]);

    // useEffect(() => {
    //     if (!financialData.periods.length && !financialData.metrics.length) {
    //         getFinancialData('annual');
    //     }
    // }, [financialData]);
    useEffect(() => {
        if (symbol) {
            setFinancialData({
                periods: [],
                metrics: []
            });
            getFinancialData(type);
        }
    }, [symbol, type]);

    const getFinancialData = async (period: string) => {
        try {
            LoaderHelper.loaderStatus(true);
            setIsLoading(true);
            // const result = await StockService.getBalanceSheetHistory(symbol, 'annual', 'accordion', 'yoy');
            const result = await StockService.getCashflowHistory(symbol, period, 'accordion');
            LoaderHelper.loaderStatus(false);
            if (result) {
                let metrics = result["ESSENTIAL"] || [];
                // Process periods based on type with 5-year limit for quarterly
                let processedPeriods: string[] = [];
                let reversedPeriods: string[] = [];
                if (metrics.length !== 0) {
                    processedPeriods = metrics[0].periods.map((p: any) => p.year.yearStr);
                    reversedPeriods = metrics[0].periods.map((p: any) => p.year.yearStr).reverse();
                }

                const filteredMetrics = metrics.map((metric: any) => {
                    if (type === 'quarter') {
                        return {
                            ...metric,
                            periods: metric.periods
                                .map((p: any) => ({
                                    year: {
                                        year: p.year.yearStr,
                                        originalYear: p.year.year,
                                        quarter: p.year.quarter
                                    },
                                    value: p.value,
                                    formattedValue: p.formattedValue
                                }))
                        };
                    } else {
                        return {
                            ...metric,
                            periods: metric.periods.map((p: any) => ({
                                year: {
                                    year: p.year.yearStr,
                                    originalYear: p.year.year,
                                    quarter: p.year.quarter
                                },
                                value: p.value,
                                formattedValue: p.formattedValue
                            }))
                        };
                    }
                });

                setFinancialData({
                    periods: reversedPeriods,
                    metrics: filteredMetrics
                });
                setTableFinancialData({
                    periods: processedPeriods,
                    metrics: filteredMetrics
                });
            }
            setIsLoading(false);
        } catch (error) {
            console.error("Error fetching financial data:", error);
        } finally {
            setIsLoading(false);
            LoaderHelper.loaderStatus(false);
        }
    }

    const handleMetricToggle = (metricId: string) => {
        setSelectedMetrics(prev => prev.includes(metricId) ? prev.filter(id => id !== metricId) : [...prev, metricId]);
    };

    const handlePeriodChange = (period: string) => {
        setType(period);
    };

    const chartAnchorRef = useRef<HTMLDivElement>(null);
    // Reserve space for the metrics table under the chart to keep prior alignment
    const tableReserve = typeof window !== 'undefined' && window.innerWidth < 768 ? 360 : 280;
    const dynamicHeight = useViewportHeight(
        () => chartAnchorRef.current?.getBoundingClientRect().top ?? 0,
        16 + tableReserve,
        420
    );

    return <div style={{ margin: '0px' }} className="bg-theme-secondary px-4 pb-4 pt-0 text-theme-primary animate-fadeIn"><div className="bg-theme-secondary rounded-lg shadow-lg overflow-visible">
        {isLoading ? (
            <div className="flex items-center justify-center h-96">
                <div className="flex flex-col items-center space-y-4">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                    <p className="text-theme-muted">Loading market trend data...</p>
                </div>
            </div>
        ) : (
            <div className="px-2.5 py-1" ref={chartAnchorRef}>
                {selectedMetrics.length > 0 && <FinancialMetricChart
                    metrics={financialData.metrics}
                    periods={financialData.periods}
                    selectedMetrics={selectedMetrics}
                    onChangePeriod={handlePeriodChange}
                    type={type}
                    height={dynamicHeight}
                />}

                <div className="flex justify-end mb-2 px-4">
                    <div className="bg-theme-tertiary/50 rounded-lg p-1 flex space-x-1 border border-theme-tertiary">
                        <button
                            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${viewMode === 'standard' ? 'bg-theme-primary text-theme-secondary shadow-sm' : 'text-theme-primary hover:bg-theme-tertiary'}`}
                            onClick={() => setViewMode('standard')}
                        >
                            Standard View
                        </button>
                        <button
                            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${viewMode === 'detailed' ? 'bg-theme-primary text-theme-secondary shadow-sm' : 'text-theme-primary hover:bg-theme-tertiary'}`}
                            onClick={() => setViewMode('detailed')}
                        >
                            Detailed View
                        </button>
                    </div>
                </div>

                {viewMode === 'standard' ? (
                    <StandardFinancialTable
                        financialData={tableFinancialData}
                        title="Cash Flow Statement"
                        selectedMetrics={selectedMetrics}
                        onMetricToggle={handleMetricToggle}
                        type="cashflow"
                    />
                ) : (
                    <FinancialMetricsTable
                        title="Cash Flow Statement"
                        isHistorical={true}
                        financialData={tableFinancialData}
                        selectedMetrics={selectedMetrics}
                        onMetricToggle={handleMetricToggle}
                    />
                )}
            </div>
        )}
    </div></div>;
}

export default CashFlowHistory;